import { fetchFile } from '@ffmpeg/util';
import {
    cleanupVideoInput,
    formatFileSize,
    isSupportedVideoFile,
    loadFFmpeg,
    prepareVideoInput,
} from '../compress/compressVideoEngine';

export {
    formatFileSize,
    isSupportedVideoFile,
};

export const MAX_WATERMARK_VIDEO_BYTES = 200 * 1024 * 1024;
export const MAX_WATERMARK_IMAGE_BYTES = 5 * 1024 * 1024;

export const POSITION_OPTIONS = [
    'top-left',
    'top-right',
    'center',
    'bottom-left',
    'bottom-right',
];

function getOverlayPosition(position = 'bottom-right', margin = 20) {
    switch (position) {
        case 'top-left':
            return { x: `${margin}`, y: `${margin}` };
        case 'top-right':
            return { x: `main_w-overlay_w-${margin}`, y: `${margin}` };
        case 'center':
            return { x: '(main_w-overlay_w)/2', y: '(main_h-overlay_h)/2' };
        case 'bottom-left':
            return { x: `${margin}`, y: `main_h-overlay_h-${margin}` };
        case 'bottom-right':
        default:
            return { x: `main_w-overlay_w-${margin}`, y: `main_h-overlay_h-${margin}` };
    }
}

function createTextWatermarkImage(text) {
    return new Promise((resolve, reject) => {
        try {
            const content = (text || '').trim();
            if (!content) {
                reject(new Error('NEED_TEXT'));
                return;
            }

            const fontSize = 56;
            const paddingX = 28;
            const paddingY = 22;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            ctx.font = `700 ${fontSize}px Arial`;
            const textWidth = Math.ceil(ctx.measureText(content).width);
            const width = textWidth + paddingX * 2;
            const height = fontSize + paddingY * 2;
            canvas.width = width;
            canvas.height = height;

            const draw = canvas.getContext('2d');
            draw.clearRect(0, 0, width, height);
            draw.font = `700 ${fontSize}px Arial`;
            draw.textBaseline = 'middle';
            draw.lineWidth = Math.max(2, Math.floor(fontSize * 0.08));
            draw.strokeStyle = 'rgba(0,0,0,0.7)';
            draw.fillStyle = '#ffffff';
            const y = height / 2;
            draw.strokeText(content, paddingX, y);
            draw.fillText(content, paddingX, y);

            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('TEXT_IMAGE_FAILED'));
                    return;
                }
                resolve(blob);
            }, 'image/png');
        } catch {
            reject(new Error('TEXT_IMAGE_FAILED'));
        }
    });
}

export function isSupportedWatermarkImageFile(file) {
    if (!file) return false;
    if (file.type?.startsWith('image/')) return true;
    return /\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name || '');
}

export function watermarkedVideoName(fileName) {
    const base = (fileName || 'video').replace(/\.[^.]+$/, '');
    return `${base}-watermarked.mp4`;
}

/**
 * @param {File} file
 * @param {{
 *   watermarkType?: 'text'|'image',
 *   text?: string,
 *   imageFile?: File|null,
 *   position?: string,
 *   opacity?: number,
 *   imageScalePercent?: number,
 * }} options
 * @param {(progress: number) => void} onProgress
 * @returns {Promise<Blob>}
 */
export async function addVideoWatermark(file, options = {}, onProgress = () => {}) {
    const {
        watermarkType = 'text',
        text = '',
        imageFile = null,
        position = 'bottom-right',
        opacity = 50,
        imageScalePercent = 20,
    } = options;

    let ffmpeg;
    let videoInput = null;
    const overlayName = watermarkType === 'image' ? 'watermark-image.png' : 'watermark-text.png';
    const outputName = 'watermarked-output.mp4';
    const alpha = Math.max(0.05, Math.min(1, opacity / 100));
    const scaleRatio = Math.max(0.05, Math.min(0.7, imageScalePercent / 100));

    const safeDelete = async (name) => {
        try { await ffmpeg.deleteFile(name); } catch (_) { /* ignore */ }
    };

    try {
        ffmpeg = await loadFFmpeg();
        videoInput = await prepareVideoInput(ffmpeg, file);

        let overlayBlob;
        if (watermarkType === 'image') {
            if (!imageFile) throw new Error('NEED_IMAGE');
            overlayBlob = imageFile;
        } else {
            overlayBlob = await createTextWatermarkImage(text);
        }

        await ffmpeg.writeFile(overlayName, await fetchFile(overlayBlob));

        const { x, y } = getOverlayPosition(position, 20);
        const progressHandler = ({ progress }) => {
            onProgress(Math.round(Math.min(progress * 100, 100)));
        };
        ffmpeg.on('progress', progressHandler);

        const filterComplex = watermarkType === 'image'
            ? `[1:v][0:v]scale2ref=w=main_w*${scaleRatio}:h=-1[wm0][base];[wm0]format=rgba,colorchannelmixer=aa=${alpha}[wm];[base][wm]overlay=${x}:${y}[v]`
            : `[1:v]format=rgba,colorchannelmixer=aa=${alpha}[wm];[0:v][wm]overlay=${x}:${y}[v]`;

        const exitCode = await ffmpeg.exec([
            '-i', videoInput.inputPath,
            '-i', overlayName,
            '-filter_complex', filterComplex,
            '-map', '[v]',
            '-map', '0:a?',
            '-c:v', 'libx264',
            '-preset', 'ultrafast',
            '-crf', '18',
            '-c:a', 'copy',
            '-movflags', '+faststart',
            outputName,
        ]);

        ffmpeg.off('progress', progressHandler);

        if (exitCode !== 0) {
            throw new Error('ENCODE_FAILED');
        }

        const data = await ffmpeg.readFile(outputName);
        if (!data || data.length === 0) {
            throw new Error('EMPTY_OUTPUT');
        }

        return new Blob([data], { type: 'video/mp4' });
    } catch (error) {
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('memory') || msg.includes('allocate') || msg.includes('oom')) {
            throw new Error('OUT_OF_MEMORY');
        }
        if (
            error?.message === 'NEED_TEXT'
            || error?.message === 'NEED_IMAGE'
            || error?.message === 'ENCODE_FAILED'
            || error?.message === 'EMPTY_OUTPUT'
            || error?.message === 'TEXT_IMAGE_FAILED'
        ) {
            throw error;
        }
        throw new Error('WATERMARK_FAILED');
    } finally {
        if (ffmpeg) {
            if (videoInput) {
                await cleanupVideoInput(ffmpeg, videoInput);
            }
            await safeDelete(overlayName);
            await safeDelete(outputName);
        }
    }
}
